import { Button, Flex, Skeleton } from "antd";
import { ErrorBoundary } from "@vyne-shared/components/ErrorBoundary";
import { FormItemGroup } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import { type NiceModalHandler, antdModalV5 } from "@ebay/nice-modal-react";
import { PatientSummaryFromId } from "molecules/PatientSummary";
import { type PaymentStatus, PaymentStatusContext } from "@vyne-shared/components/PaymentStatusContext";
import { Suspense, lazy, useMemo, useState } from "react";
import { TakePaymentRadioTabs } from "../NewPaymentModal/TakePaymentRadioTabs/TakePaymentRadioTabs";
import { VyneModal } from "@vyne-shared/components/vyneModal/VyneModal";
import { useSharedTransactionInvalidation } from "utils/useSharedTransactionInvalidation";
import type { PayNowModalProps } from "./CreatePayNowModal";
// import { usePayment } from "@vyne-shared/components/Payment/usePaymentContext";

const PayNowApp = lazy(() => import("./PayNowApp"));

const FrameWidth = 480;

const modalProps = Object.freeze({
    width: FrameWidth + 60,
    style: Object.freeze({ top: 4 }),
});

interface WithModal extends PayNowModalProps {
    readonly modal: NiceModalHandler;
}

/** Modal dialog content for entering and submitting a manual payment. */
export const PayNowModalContent = ({ patientId, modal, setTakePaymentTab, takePaymentTab }: WithModal) => {
    const [status, setStatus] = useState<PaymentStatus>("data-entry");

    // Invalidate queries that are affected by posting a sale, or could in the future be affected by a sale
    // failing or partially failing.
    // Queries are invalidated immediately after success or error, so they can refresh in the background.
    useSharedTransactionInvalidation("payment", patientId, null, status === "success" || status === "error");

    // Ensure context value doesn't change on every render
    const statusContext = useMemo(
        () => ({ status, setStatus, hideModal: () => void modal.hide() }),
        [status, setStatus, modal],
    );

    return (
        <VyneModal
            {...modalProps}
            {...antdModalV5(modal)}
            title="Take a Payment"
            closable={status !== "submitting"}
            footer={status === "data-entry" ? <PayNowFooter onClick={() => void modal.hide()} /> : null}
        >
            {status === "data-entry" && setTakePaymentTab && takePaymentTab && (
                <TakePaymentRadioTabs setTakePaymentTab={setTakePaymentTab} takePaymentTab={takePaymentTab} />
            )}
            <Flex vertical={true} gap="1rem">
                {status === "data-entry" ? <PatientSummaryFromId id={patientId} /> : null}
                <PaymentStatusContext value={statusContext}>
                    <ErrorBoundary resettable payNow hideModal={() => void modal.hide()}>
                        <Suspense
                            fallback={
                                <FormItemGroup legendTitle="Payment Details">
                                    <Skeleton active data-testid="pay-now-modal-suspense-loading-skeleton" />
                                </FormItemGroup>
                            }
                        >
                            <PayNowApp patientId={patientId} />
                        </Suspense>
                    </ErrorBoundary>
                </PaymentStatusContext>
            </Flex>
        </VyneModal>
    );
};

const PayNowFooter = ({ onClick }: { readonly onClick: () => void }) => (
    <Button type="default" onClick={onClick}>
        Cancel
    </Button>
);
